import { shallowReactive } from 'vue'

const DEFAULT_DURATION = 3000
const MAX_VISIBLE_TOASTS = 3
const toastTimers = new Map()
let toastSeed = 0

export const toastItems = shallowReactive([])

const TYPE_ALIASES = {
  info: 'info',
  normal: 'info',
  toast: 'info',
  success: 'success',
  error: 'error',
  warn: 'warn',
  warning: 'warn',
  loading: 'loading',
}

const DIRECTIONS = new Set(['top', 'center', 'bottom'])
const LAYOUTS = new Set(['vertical', 'horizontal'])

function isClientEnvironment() {
  return typeof window !== 'undefined'
}

function normalizeType(type) {
  return TYPE_ALIASES[type] || 'info'
}

function normalizeContent(content) {
  if (content === null || content === undefined) return ''
  return typeof content === 'string' ? content : String(content)
}

function normalizeConfig(input, fallbackType, previous = {}) {
  const raw = typeof input === 'object' && input !== null ? input : { content: input }
  const type = normalizeType(raw.type || fallbackType || previous.type)
  const durationFallback = type === 'loading' ? 0 : DEFAULT_DURATION
  const typeChanged = Boolean(previous.type && previous.type !== type)
  const duration = Number.isFinite(raw.duration)
    ? Math.max(0, raw.duration)
    : typeChanged
      ? durationFallback
      : (previous.duration ?? durationFallback)
  const direction = DIRECTIONS.has(raw.direction)
    ? raw.direction
    : DIRECTIONS.has(raw.position)
      ? raw.position
      : (previous.direction ?? 'center')
  const layout = LAYOUTS.has(raw.layout) ? raw.layout : (previous.layout ?? 'vertical')

  return {
    ...previous,
    id: raw.id || previous.id || `toast-${Date.now()}-${++toastSeed}`,
    type,
    content: normalizeContent(raw.content ?? previous.content),
    duration,
    direction,
    layout,
    icon: raw.icon ?? previous.icon ?? null,
    className: raw.className || raw.class || previous.className || '',
    closable: raw.closable ?? previous.closable ?? false,
    disableBodyTouch:
      raw.disableBodyTouch ?? (typeChanged ? false : (previous.disableBodyTouch ?? false)),
    onClose: raw.onClose ?? previous.onClose,
    createdAt: previous.createdAt || Date.now(),
  }
}

function clearTimer(id) {
  const timer = toastTimers.get(id)
  if (timer) {
    clearTimeout(timer)
    toastTimers.delete(id)
  }
}

function scheduleClose(toast) {
  clearTimer(toast.id)
  if (toast.duration <= 0) return

  const timer = setTimeout(() => closeToast(toast.id), toast.duration)
  toastTimers.set(toast.id, timer)
}

function createHandle(id) {
  return {
    id,
    close: () => closeToast(id),
    update: (config) => updateToast(id, config),
  }
}

function removeOldestToast() {
  const removableToast = toastItems.find((item) => item.type !== 'loading') || toastItems[0]
  if (removableToast) closeToast(removableToast.id)
}

function openToast(input, fallbackType = 'info') {
  if (!isClientEnvironment()) return createHandle(null)

  const toast = normalizeConfig(input, fallbackType)
  const hasCustomId = typeof input === 'object' && input !== null && Boolean(input.id)
  const customIdIndex = hasCustomId ? toastItems.findIndex((item) => item.id === toast.id) : -1

  if (customIdIndex >= 0) {
    const currentToast = toastItems[customIdIndex]
    const updatedToast = normalizeConfig(input, fallbackType, currentToast)
    updatedToast.id = currentToast.id
    toastItems.splice(customIdIndex, 1, updatedToast)
    scheduleClose(updatedToast)
    return createHandle(updatedToast.id)
  }

  const duplicateIndex = toastItems.findIndex(
    (item) => !hasCustomId && item.type === toast.type && item.content === toast.content
  )

  if (duplicateIndex >= 0) {
    const duplicate = normalizeConfig(input, fallbackType, toastItems[duplicateIndex])
    toastItems.splice(duplicateIndex, 1, duplicate)
    scheduleClose(duplicate)
    return createHandle(duplicate.id)
  }

  while (toastItems.length >= MAX_VISIBLE_TOASTS) removeOldestToast()
  toastItems.push(toast)
  scheduleClose(toast)
  return createHandle(toast.id)
}

export function closeToast(id) {
  if (!id) return

  const index = toastItems.findIndex((item) => item.id === id)
  if (index < 0) return

  const [toast] = toastItems.splice(index, 1)
  clearTimer(id)
  toast.onClose?.(id)
}

export function updateToast(id, config) {
  if (!id) return createHandle(null)

  const index = toastItems.findIndex((item) => item.id === id)
  if (index < 0) return createHandle(id)

  const currentToast = toastItems[index]
  const nextToast = normalizeConfig(config, currentToast.type, currentToast)
  nextToast.id = currentToast.id
  toastItems.splice(index, 1, nextToast)
  scheduleClose(nextToast)
  return createHandle(id)
}

function clearToasts() {
  const ids = toastItems.map((item) => item.id)
  const errors = []

  ids.forEach((id) => {
    try {
      closeToast(id)
    } catch (error) {
      errors.push(error)
    }
  })

  if (errors.length === 1) throw errors[0]
  if (errors.length > 1) throw new AggregateError(errors, '关闭 Toast 时发生错误')
}

function pauseToast(id) {
  clearTimer(id)
}

function resumeToast(id) {
  const toast = toastItems.find((item) => item.id === id)
  if (toast) scheduleClose(toast)
}

/**
 * @description 全局移动端轻提示统一入口，支持常用配置、更新与关闭。
 * @returns {Object} Toast 命令式调用方法集合
 */
export const Toast = {
  toast: (config) => openToast(config, 'info'),
  open: (config) => openToast(config, config?.type || 'info'),
  normal: (config) => openToast(config, 'info'),
  info: (config) => openToast(config, 'info'),
  success: (config) => openToast(config, 'success'),
  error: (config) => openToast(config, 'error'),
  warn: (config) => openToast(config, 'warn'),
  warning: (config) => openToast(config, 'warn'),
  loading: (config) => openToast(config, 'loading'),
  close: closeToast,
  clear: clearToasts,
  closeAll: clearToasts,
  pause: pauseToast,
  resume: resumeToast,
}
